/**
 * NowLineBanner — cross-case urgency readout for the accountant WorkDesk.
 *
 * The NEW, law-compliant Now-Line for the WorkDesk (crimson = severity / Needs Your Attention,
 * hueless --color-verified = No Action Needed, squared 2px, no motion, DM Mono counts, all-clear
 * compresses to one 44px line). It COEXISTS with the older NowLineHeader (still used by
 * AccountantDashboard) — the old surfaces are NOT retired here; retirement is the owner's call,
 * made once the new WorkDesk is complete. Presentational — the parent owns the /queue/nowline-stats fetch.
 */
import { AlertTriangle, CheckCircle } from 'lucide-react';

interface NowLineCategory { label: string; count: number }
interface NowLineSide { count?: number; breakdown?: NowLineCategory[] }
interface NowLineStats { needs_attention?: NowLineSide; no_action_needed?: NowLineSide }

const sum = (s?: NowLineSide) => s?.count ?? 0;

export default function NowLineBanner({ data, loading }: { data: NowLineStats | null; loading: boolean }) {
  // loading: static skeleton, NO pulse animation (motion law)
  if (loading) {
    return (
      <div className="mb-4 min-h-[44px] flex items-center gap-3 px-4 bg-bg-surface border border-border rounded-[2px]">
        <span className="h-3 w-36 bg-bg-elevated rounded-[2px]" />
        <span className="h-3 w-24 bg-bg-elevated rounded-[2px]" />
      </div>
    );
  }
  // error / missing: fail quiet — advisory surface never blocks the desk
  if (!data) return null;

  const attention = sum(data.needs_attention);
  const clear = sum(data.no_action_needed);
  const attCats = data.needs_attention?.breakdown ?? [];
  const clearCats = data.no_action_needed?.breakdown ?? [];

  // ── ALL-CLEAR: single quiet line, no split, no title ──
  if (attention === 0) {
    return (
      <div role="status" className="mb-4 min-h-[44px] flex items-center gap-2 px-4 bg-bg-surface border border-border rounded-[2px]">
        <CheckCircle className="w-3.5 h-3.5" style={{ color: 'var(--color-verified)' }} />
        <span className="text-xs text-text-secondary">No action needed</span>
        <span className="ml-auto flex items-baseline gap-1.5">
          <span className="font-mono text-[11px]" style={{ color: 'var(--color-verified)' }}>{clear}</span>
          <span className="text-[11px] text-text-dim">clear</span>
        </span>
      </div>
    );
  }

  // ── ATTENTION: severity bar + two-zone split ──
  return (
    <div role="status" aria-label={`${attention} items need your attention`}
      className="relative mb-4 px-4 py-3 bg-bg-surface border border-border rounded-[2px]">
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] rounded-l-[2px]"
        style={{ backgroundColor: 'var(--color-blocker)' }} />
      <div className="grid grid-cols-2 divide-x divide-border max-sm:grid-cols-1 max-sm:divide-x-0 max-sm:divide-y max-sm:gap-3">
        {/* zone 1 — severity */}
        <div className="pr-4 max-sm:pr-0">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--color-blocker)' }} />
            <span className="text-[11px] uppercase tracking-wide font-medium text-text-secondary">Needs Your Attention</span>
            <span className="ml-2 font-mono text-sm font-medium" style={{ color: 'var(--color-blocker)' }}>{attention}</span>
          </div>
          {attCats.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {attCats.map((c) => (
                <span key={c.label}
                  className="inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded-[2px] border text-[11px]"
                  style={{ borderColor: 'var(--color-blocker-tint-bd)', backgroundColor: 'var(--color-blocker-tint)' }}>
                  <span className="text-text-primary">{c.label}</span>
                  <span className="font-mono" style={{ color: 'var(--color-blocker)' }}>{c.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        {/* zone 2 — quiet */}
        <div className="pl-4 max-sm:pl-0 max-sm:pt-3">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5" style={{ color: 'var(--color-verified)' }} />
            <span className="text-[11px] uppercase tracking-wide font-medium text-text-dim">No Action Needed</span>
            <span className="ml-2 font-mono text-sm" style={{ color: 'var(--color-verified)' }}>{clear}</span>
          </div>
          {clearCats.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {clearCats.map((c) => (
                <span key={c.label}
                  className="inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded-[2px] border border-border text-[11px]">
                  <span className="text-text-dim">{c.label}</span>
                  <span className="font-mono" style={{ color: 'var(--color-verified)' }}>{c.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
